import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import Select from 'react-select';

interface TagFieldProps {
  name: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  options: any[];
  valueExpr?: string;
  displayExpr?: string;
}

export const TagField = React.forwardRef<any, TagFieldProps>(
  (
    {
      name,
      label,
      placeholder,
      disabled = false,
      required = false,
      className,
      options,
      valueExpr = 'id',
      displayExpr = 'name',
    },
    ref
  ) => {
    const { control, formState: { errors } } = useFormContext();
    const error = errors[name];

    const normalizedOptions = React.useMemo(() => {
        if (!options) return [];
        return options.map((item: any) => ({
            value: item[valueExpr],
            label: item[displayExpr],
            ...item,
        }));
    }, [options, valueExpr, displayExpr]);

    return (
      <div className={cn('space-y-2', className)}>
        {label && (
          <Label htmlFor={name}>
            {label}
            {required && <span className="text-destructive ml-1">*</span>}
          </Label>
        )}
        <Controller
            name={name}
            control={control}
            render={({ field }) => {
                const selectedOptions = normalizedOptions.filter(opt => field.value?.includes(opt.value));
                return (
                    <Select
                        inputId={name}
                        isMulti
                        isDisabled={disabled}
                        isClearable={true}
                        isSearchable={true}
                        placeholder={placeholder || 'Chọn...'}
                        value={selectedOptions}
                        options={normalizedOptions}
                        onChange={(selected: any) => {
                            const values = selected ? selected.map((s: any) => s.value) : [];
                            field.onChange(values);
                        }}
                        classNamePrefix="react-select"
                        styles={{
                            control: (base, state) => ({
                                ...base,
                                minHeight: '40px',
                                borderRadius: '0.5rem',
                                borderColor: error ? 'hsl(var(--destructive))' : state.isFocused ? 'hsl(var(--ring))' : 'hsl(var(--input))',
                                boxShadow: state.isFocused && error 
                                    ? '0 0 0 1px hsl(var(--destructive))' 
                                    : state.isFocused 
                                    ? '0 0 0 1px hsl(var(--ring))' 
                                    : 'none',
                                '&:hover': {
                                    borderColor: error ? 'hsl(var(--destructive))' : 'hsl(var(--input))',
                                }
                            }),
                            menu: (base) => ({
                                ...base,
                                borderRadius: '0.5rem',
                                overflow: 'hidden',
                                zIndex: 50,
                            }),
                        }}
                    />
                );
            }}
        />
        {error && (
          <p className="text-sm text-destructive">{String(error.message)}</p>
        )}
      </div>
    );
  }
);

TagField.displayName = 'TagField';

export default TagField;
